/**
 * Goal Progress Card (Epic 4: Dynamic Progress)
 *
 * Redesigned with Information Hierarchy:
 *   Hero number (pages_remaining_today)
 *   → Suggestion message
 *   → Today's progress bar
 *   → Checkpoint box (adaptive per status)
 *   → Overall progress bar
 *   → Expandable details (power-user data)
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { goalsService } from '@/services/goals.service';
import { PAGES_PER_KHATM, type ProgressStatus } from '@/types/goals';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Target,
  Settings,
  ArrowRight,
  ChevronDown,
  MapPin,
  Clock,
  BookOpen,
  Sparkles,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { EditGoalDrawer } from './EditGoalDrawer';
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/* ------------------------------------------------------------------ */
/*  Status configuration                                               */
/* ------------------------------------------------------------------ */

interface StatusConfig {
  color: string;
  bgGlow: string;
  label: string;
  icon: typeof Target;
}

function getStatusConfig(status: ProgressStatus): StatusConfig {
  switch (status) {
    case 'on_track':
      return {
        color: 'bg-green-500',
        bgGlow: 'from-green-500/10 via-transparent to-transparent',
        label: 'ทันแผน',
        icon: BookOpen,
      };
    case 'ahead':
      return {
        color: 'bg-blue-500',
        bgGlow: 'from-blue-500/10 via-transparent to-transparent',
        label: 'เร็วกว่าแผน',
        icon: Sparkles,
      };
    case 'behind':
      return {
        color: 'bg-destructive',
        bgGlow: 'from-orange-500/10 via-transparent to-transparent',
        label: 'ล่าช้า',
        icon: AlertTriangle,
      };
    case 'overdue':
      return {
        color: 'bg-muted border border-muted-foreground/30',
        bgGlow: 'from-muted/30 via-transparent to-transparent',
        label: 'เลยกำหนด',
        icon: Clock,
      };
    default:
      return {
        color: 'bg-muted',
        bgGlow: 'from-muted/20 via-transparent to-transparent',
        label: 'ไม่ทราบสถานะ',
        icon: Target,
      };
  }
}

/* ------------------------------------------------------------------ */
/*  Helpers: cumulative pages → Khatm round + Quran page (1-604)       */
/* ------------------------------------------------------------------ */

function formatKhatmPage(cumulativePages: number, khatmTarget: number) {
  if (cumulativePages <= 0) return { round: 1, page: 0 };
  const round = Math.min(khatmTarget, Math.floor((cumulativePages - 1) / PAGES_PER_KHATM) + 1);
  const page = cumulativePages <= PAGES_PER_KHATM
    ? cumulativePages
    : ((cumulativePages - 1) % PAGES_PER_KHATM) + 1;
  return { round, page };
}

function KhatmPageLabel({ cumulativePages, khatmTarget }: { cumulativePages: number; khatmTarget: number }) {
  const { round, page } = formatKhatmPage(cumulativePages, khatmTarget);
  const showRound = khatmTarget > 1;
  return (
    <>
      {showRound && <span className="text-muted-foreground font-normal">รอบ {round} ·</span>}{' '}
      หน้า {page.toLocaleString('th-TH')}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Checkpoint Box — adaptive per status                               */
/* ------------------------------------------------------------------ */

interface CheckpointBoxProps {
  status: ProgressStatus;
  expectedLastPage?: number;
  pagesReadTotal: number;
  lastReadPage: number;
  nextPageToRead: number;
  pagesAheadOrBehind?: number;
  khatmTarget: number;
}

function CheckpointBox({
  status,
  expectedLastPage,
  pagesReadTotal,
  lastReadPage,
  nextPageToRead,
  pagesAheadOrBehind,
  khatmTarget,
}: CheckpointBoxProps) {
  const gap = pagesAheadOrBehind ?? 0;
  const showCheckpoint = status !== 'overdue' && typeof expectedLastPage === 'number';
  const showGap = (status === 'behind' || status === 'ahead') && gap !== 0;
  const isMultiKhatm = khatmTarget > 1;

  return (
    <div className="rounded-xl bg-muted/40 border border-border p-3 space-y-2">
      {showCheckpoint && (
        <div className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" />
            วันนี้ต้องถึง
          </span>
          <span className="font-bold text-foreground">
            {isMultiKhatm
              ? <KhatmPageLabel cumulativePages={expectedLastPage} khatmTarget={khatmTarget} />
              : <>หน้า {expectedLastPage.toLocaleString('th-TH')}</>
            }
          </span>
        </div>
      )}

      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          ตอนนี้อยู่ที่
        </span>
        <span className="font-bold text-foreground flex items-center gap-1">
          {isMultiKhatm
            ? <KhatmPageLabel cumulativePages={pagesReadTotal} khatmTarget={khatmTarget} />
            : <>หน้า {lastReadPage.toLocaleString('th-TH')}</>
          }
          {status === 'ahead' && showCheckpoint && pagesReadTotal > (expectedLastPage ?? 0) && (
            <span className="text-green-500"> ✓</span>
          )}
        </span>
      </div>

      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">หน้าถัดไป</span>
        <span className="font-bold text-primary flex items-center gap-1">
          หน้า {nextPageToRead.toLocaleString('th-TH')} <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </div>

      {showGap && (
        <div className={cn(
          'flex items-center justify-between text-sm pt-1 border-t border-border/50',
          gap > 0 ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'
        )}>
          <span className="flex items-center gap-1">
            {gap > 0 ? '✨ เร็วกว่าแผน' : '⚠️ ช้ากว่าแผน'}
          </span>
          <span className="font-bold">
            {Math.abs(gap).toLocaleString('th-TH')} หน้า
          </span>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Expandable Details                                                 */
/* ------------------------------------------------------------------ */

interface ExpandableDetailsProps {
  baselineDaily?: number;
  dynamicDaily: number;
  diff?: number;
  khatmCompleted: number;
  khatmTarget: number;
  pagesReadTotal: number;
  totalTargetPages: number;
  sessionsToday: number;
  checkpoint?: number;
  status: ProgressStatus;
}

function ExpandableDetails({
  baselineDaily,
  dynamicDaily,
  diff,
  khatmCompleted,
  khatmTarget,
  pagesReadTotal,
  totalTargetPages,
  sessionsToday,
}: ExpandableDetailsProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-t border-border pt-3">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-full"
      >
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
        ดูรายละเอียด
      </button>

      {open && (
        <div className="mt-3 space-y-2 text-sm animate-in fade-in slide-in-from-top-1 duration-200">
          {typeof baselineDaily === 'number' && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">เป้าตายตัว</span>
              <span className="text-foreground">{baselineDaily.toLocaleString('th-TH')} หน้า/วัน</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">เป้าปรับแล้ว</span>
            <span className="text-foreground">{dynamicDaily.toLocaleString('th-TH')} หน้า/วัน</span>
          </div>
          {typeof diff === 'number' && diff !== 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">ส่วนต่าง</span>
              <span className={cn(
                'font-medium',
                diff > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'
              )}>
                {diff > 0 ? '+' : ''}{diff.toLocaleString('th-TH')} หน้า/วัน
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">Khatm</span>
            <span className="text-foreground">{khatmCompleted}/{khatmTarget} จบ</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">อ่านรวม</span>
            <span className="text-foreground">
              {pagesReadTotal.toLocaleString('th-TH')}/{totalTargetPages.toLocaleString('th-TH')} หน้า
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">เซสชันวันนี้</span>
            <span className="text-foreground">{sessionsToday.toLocaleString('th-TH')} ครั้ง</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export function GoalProgressCard() {
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [wrapUpDialogOpen, setWrapUpDialogOpen] = useState(false);
  const [wrapUpLoading, setWrapUpLoading] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: progressData, isLoading: progressLoading } = useQuery({
    queryKey: ['goals', 'current', 'progress'],
    queryFn: () => goalsService.getCurrentGoalProgress(),
    refetchInterval: 60000,
  });

  const { data: currentGoalData } = useQuery({
    queryKey: ['goals', 'current'],
    queryFn: () => goalsService.getCurrentGoal(),
  });

  if (progressLoading) {
    return (
      <Card className="bg-card border-border overflow-hidden">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-32 bg-muted rounded" />
            <div className="flex justify-center">
              <div className="h-20 w-28 bg-muted rounded-xl" />
            </div>
            <div className="h-3 w-full bg-muted rounded" />
            <div className="h-3 w-2/3 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!progressData) {
    return (
      <Card className="bg-card border-border overflow-hidden">
        <CardContent className="p-6 text-center">
          <Target className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground mb-4">ยังไม่มีเป้าหมาย</p>
          <Button asChild variant="default">
            <Link to="/set-goal">ตั้งเป้าหมาย</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const totalTargetPages = progressData.khatm_target * PAGES_PER_KHATM;
  const overallProgress = totalTargetPages > 0
    ? (progressData.pages_read_total / totalTargetPages) * 100
    : 0;
  const todayProgress = progressData.daily_target_pages > 0
    ? (progressData.pages_read_today / progressData.daily_target_pages) * 100
    : 0;

  const statusConfig = getStatusConfig(progressData.status);
  const isOverdue = progressData.status === 'overdue';

  const heroLabel = isOverdue ? 'เหลืออีก' : 'เหลือวันนี้';
  const heroSublabel = isOverdue ? 'หน้าถึงจบ' : 'หน้า';
  const heroValue = isOverdue
    ? Math.max(0, totalTargetPages - progressData.pages_read_total)
    : Math.max(0, progressData.pages_remaining_today);

  const showTodayBar = !isOverdue;

  // Checkpoint bar logic:
  // "pages I need to read TODAY to reach checkpoint" vs "daily target"
  // When checkpoint requires more than daily target → show combined bar with marker
  const startOfTodayTotal = progressData.pages_read_total - progressData.pages_read_today;
  const pagesForCheckpointToday = typeof progressData.expected_last_page_by_today === 'number'
    ? Math.max(0, progressData.expected_last_page_by_today - startOfTodayTotal)
    : 0;
  const showCheckpointBar = !isOverdue
    && pagesForCheckpointToday > progressData.daily_target_pages
    && pagesForCheckpointToday > 0;

  // Marker position: where daily target sits on the checkpoint bar (as %)
  const dailyMarkerPercent = showCheckpointBar
    ? (progressData.daily_target_pages / pagesForCheckpointToday) * 100
    : 100;
  // Fill: how much the user has read today vs checkpoint total
  const checkpointFillPercent = showCheckpointBar
    ? (progressData.pages_read_today / pagesForCheckpointToday) * 100
    : 0;

  const dailyComplete = progressData.pages_remaining_today <= 0;
  const gapToCheckpoint = typeof progressData.expected_last_page_by_today === 'number'
    ? Math.max(0, progressData.expected_last_page_by_today - progressData.pages_read_total)
    : 0;
  const hasBonusGap = !isOverdue && gapToCheckpoint > 0 && dailyComplete;

  return (
    <div className="space-y-0">
      <Card className="bg-card border-primary/20 overflow-hidden relative shadow-lg">
        {/* Texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.03] dark:opacity-[0.02] pointer-events-none"
          style={{
            backgroundImage: 'url(/texture.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'multiply',
          }}
        />

        {/* Subtle status glow */}
        <div className={cn(
          'absolute inset-0 bg-gradient-to-b opacity-40 pointer-events-none',
          statusConfig.bgGlow,
        )} />

        <CardContent className="p-5 relative z-10 space-y-4">
          {/* Header: Status badge + Edit button */}
          <div className="flex items-center justify-between">
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold text-white',
                statusConfig.color,
              )}
            >
              <statusConfig.icon className="h-3 w-3" />
              {statusConfig.label}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full h-8 px-3 text-xs text-muted-foreground"
              onClick={() => setEditDrawerOpen(true)}
            >
              <Settings className="h-3.5 w-3.5 mr-1" />
              แก้ไข
            </Button>
          </div>

          {/* Hero Number */}
          <div className="text-center py-2">
            {!dailyComplete ? (
              <>
                <p className="text-xs text-muted-foreground mb-1">{heroLabel}</p>
                <p className="text-6xl font-extrabold text-foreground leading-none tracking-tight">
                  {heroValue.toLocaleString('th-TH')}
                </p>
                <p className="text-sm text-muted-foreground mt-1">{heroSublabel}</p>
              </>
            ) : hasBonusGap ? (
              <>
                <p className="text-xs text-green-600 dark:text-green-400 mb-1">✅ ครบเป้ารายวันแล้ว!</p>
                <p className="text-6xl font-extrabold text-foreground leading-none tracking-tight">
                  +{gapToCheckpoint.toLocaleString('th-TH')}
                </p>
                <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">หน้าถึง checkpoint</p>
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mb-1">{heroLabel}</p>
                <p className="text-6xl font-extrabold text-foreground leading-none tracking-tight">
                  {heroValue.toLocaleString('th-TH')}
                </p>
                <p className="text-sm text-green-600 dark:text-green-400 mt-1">✅ ครบแล้ว!</p>
              </>
            )}
          </div>

          {/* Suggestion Message */}
          {progressData.suggestion_message && (
            <p className={cn(
              'text-sm text-center leading-relaxed px-2',
              isOverdue ? 'text-muted-foreground' : 'text-foreground',
            )}>
              {progressData.suggestion_message}
            </p>
          )}

          {/* Today's Progress Bar */}
          {showTodayBar && (
            <div className="space-y-1.5">
              {showCheckpointBar ? (
                <>
                  {/* Single bar: checkpoint is 100%, daily target is a marker */}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      อ่านแล้ว {progressData.pages_read_today.toLocaleString('th-TH')} หน้า
                    </span>
                    <span className="text-muted-foreground">
                      checkpoint หน้า {progressData.expected_last_page_by_today!.toLocaleString('th-TH')}
                    </span>
                  </div>
                  <div className="relative h-3 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn(
                        'absolute inset-y-0 left-0 rounded-full transition-all duration-500',
                        checkpointFillPercent >= dailyMarkerPercent ? 'bg-green-500' : 'bg-primary',
                      )}
                      style={{ width: `${Math.min(100, checkpointFillPercent)}%` }}
                    />
                    {/* Marker line at daily target position */}
                    <div
                      className="absolute inset-y-0 w-0.5 bg-foreground/50 z-10"
                      style={{ left: `${dailyMarkerPercent}%` }}
                    >
                      <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 text-[9px] text-muted-foreground whitespace-nowrap">
                        เป้าวัน
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-[10px]">
                    <span className={cn(
                      dailyComplete ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-muted-foreground'
                    )}>
                      {dailyComplete
                        ? '✅ ครบเป้ารายวัน'
                        : `เป้ารายวัน ${progressData.daily_target_pages.toLocaleString('th-TH')} หน้า`}
                    </span>
                    <span className="text-orange-600 dark:text-orange-400">
                      อีก {gapToCheckpoint.toLocaleString('th-TH')} หน้าถึง checkpoint
                    </span>
                  </div>
                </>
              ) : (
                <>
                  {/* Normal bar: daily target only (no checkpoint gap) */}
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      อ่านแล้ว {progressData.pages_read_today.toLocaleString('th-TH')}/{progressData.daily_target_pages.toLocaleString('th-TH')} หน้า
                    </span>
                    <span className={cn(
                      'font-bold',
                      todayProgress >= 100 ? 'text-green-600 dark:text-green-400' : 'text-primary',
                    )}>
                      {todayProgress >= 100 ? '✅' : `${Math.min(100, Math.round(todayProgress)).toLocaleString('th-TH')}%`}
                    </span>
                  </div>
                  <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all duration-500',
                        todayProgress >= 100 ? 'bg-green-500' : 'bg-primary',
                      )}
                      style={{ width: `${Math.min(100, todayProgress)}%` }}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Overdue: overall bar instead */}
          {isOverdue && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  อ่านแล้ว {progressData.pages_read_total.toLocaleString('th-TH')}/{totalTargetPages.toLocaleString('th-TH')} หน้า
                </span>
                <span className="font-bold text-primary">
                  {Math.min(100, Math.round(overallProgress)).toLocaleString('th-TH')}%
                </span>
              </div>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full transition-all duration-500 bg-primary"
                  style={{ width: `${Math.min(100, overallProgress)}%` }}
                />
              </div>
            </div>
          )}

          {/* Checkpoint Box (adaptive) */}
          <CheckpointBox
            status={progressData.status}
            expectedLastPage={progressData.expected_last_page_by_today}
            pagesReadTotal={progressData.pages_read_total}
            lastReadPage={progressData.last_read_page}
            nextPageToRead={progressData.next_page_to_read}
            pagesAheadOrBehind={progressData.pages_ahead_or_behind}
            khatmTarget={progressData.khatm_target}
          />

          {/* Overdue CTA */}
          {isOverdue && (
            <div className="flex flex-col gap-2">
              <Button
                variant="default"
                size="sm"
                className="w-full"
                onClick={() => setEditDrawerOpen(true)}
              >
                <Clock className="h-4 w-4 mr-2" />
                ขยายเวลาเป้าหมาย
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setWrapUpDialogOpen(true)}
              >
                สรุปและตั้งเป้าใหม่
              </Button>
            </div>
          )}

          {/* Overall Progress (non-overdue) */}
          {!isOverdue && (
            <div className="space-y-1.5 pt-2 border-t border-border">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  ภาพรวม {Math.round(overallProgress).toLocaleString('th-TH')}%
                </span>
                <span className="text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  เหลือ {progressData.days_remaining.toLocaleString('th-TH')} วัน
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full transition-all duration-500 bg-primary/60"
                  style={{ width: `${Math.min(100, overallProgress)}%` }}
                />
              </div>
            </div>
          )}

          {/* Expandable Details */}
          <ExpandableDetails
            baselineDaily={progressData.baseline_daily_target_pages}
            dynamicDaily={progressData.daily_target_pages}
            diff={progressData.daily_target_pages_diff}
            khatmCompleted={progressData.khatm_completed}
            khatmTarget={progressData.khatm_target}
            pagesReadTotal={progressData.pages_read_total}
            totalTargetPages={totalTargetPages}
            sessionsToday={progressData.sessions_count_today}
            checkpoint={progressData.expected_last_page_by_today}
            status={progressData.status}
          />
        </CardContent>
      </Card>

      <EditGoalDrawer
        open={editDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        goal={currentGoalData || null}
      />

      <Dialog open={wrapUpDialogOpen} onOpenChange={setWrapUpDialogOpen}>
        <DialogPortal>
          <DialogOverlay />
          <DialogContent onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>สรุปเป้าหมายนี้</DialogTitle>
            <DialogDescription>
              ต้องการสรุปเป้าหมายปัจจุบันและดูสถิติที่ทำได้หรือไม่? หลังจากนั้นคุณสามารถตั้งเป้าหมายใหม่ได้
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setWrapUpDialogOpen(false)}
              disabled={wrapUpLoading}
            >
              ยกเลิก
            </Button>
            <Button
              onClick={async () => {
                if (!currentGoalData?.id) return;
                setWrapUpLoading(true);
                try {
                  await goalsService.completeGoal(currentGoalData.id);
                  await queryClient.invalidateQueries({ queryKey: ['goals'] });
                  setWrapUpDialogOpen(false);
                  navigate(`/goal-completed/${currentGoalData.id}?partial=1`, { replace: true });
                } catch {
                  setWrapUpLoading(false);
                }
              }}
              disabled={wrapUpLoading}
            >
              {wrapUpLoading ? 'กำลังดำเนินการ...' : 'สรุปและดูสถิติ'}
            </Button>
          </DialogFooter>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </div>
  );
}

export default GoalProgressCard;
